import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Core Slice Actions
import {
  setCurrentPage,
  setSelectedSessionMaster,
} from "../../redux/admin/sessionMaster.slice";

// Redux Operation Thunks
import {
  fetchSessionMasters,
  addSessionMaster,
  updateSessionMaster,
} from "../../redux/admin/sessionMaster.thunk";

// Cross-Slice Master Thunk Imports
import { fetchStudents } from "../../redux/admin/student.thunk";
import { fetchCourseCategories } from "../../redux/admin/courseCategory.thunk";
import { fetchCourseInstructors } from "../../redux/admin/courseInstructor.thunk";
import { fetchBranches } from "../../redux/admin/branch.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "sm-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.22" strokeWidth={thickness} />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth={thickness} strokeLinecap="round" />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "sm-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const SessionMaster = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX SELECTORS
  ====================================================== */
  const sessionMasterState = useSelector((state) => state.admin?.sessionMaster || {});
  const studentsState = useSelector((state) => state.admin?.students || {});
  const courseCategoryState = useSelector((state) => state.admin?.courseCategory || {});
  const branchState = useSelector((state) => state.admin?.branches || {});
  const courseInstructorState = useSelector((state) => state.admin?.courseInstructor || {});

  const globalStudents = studentsState.studentList || studentsState.students || [];
  const globalCategories = courseCategoryState.courseCategories || courseCategoryState.categories || [];
  const globalBranches = branchState.branches || branchState.branchList || [];
  const globalInstructors = courseInstructorState.courseInstructors || courseInstructorState.data || courseInstructorState.instructors || [];

  const {
    sessionMasters = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedSessionMaster = null,
  } = sessionMasterState;

  /* =====================================================
     SEARCH & FILTER STATES
  ====================================================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive

  /* =====================================================
     MODAL & TABS STATE
  ====================================================== */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [saving, setSaving] = useState(false);

  /* =====================================================
     INITIAL FORM STATE
  ====================================================== */
  const initialFormState = {
    course_id: "",
    branch_id: "",
    block_id: "",
    sport_id: "",
    session_name: "",
    category: "",
    instructor_id: "",
    mode: "offline",
    location: "",
    meeting_link: "",
    start_date: "",
    end_date: "",
    max_students: "",
    status: "active",
    schedule: [{ day_of_week: "Tuesday", start_time: "", end_time: "" }],
    students: [],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [studentSearch, setStudentSearch] = useState("");
  const [instructorSearch, setInstructorSearch] = useState("");

  /* =====================================================
     SEARCH DEBOUNCE EFFECT
  ====================================================== */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* =====================================================
     FETCH LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(fetchSessionMasters({
      page: currentPage,
      limit,
      search: debouncedSearch,
      status: activeFilter === "all" ? "" : activeFilter,
    }));
  }, [dispatch, currentPage, debouncedSearch, activeFilter]);

  useEffect(() => {
    dispatch(fetchStudents({ page: 1, limit: 1000, search: "", status: "active" }));
    dispatch(fetchCourseCategories());
    dispatch(fetchCourseInstructors());
    dispatch(fetchBranches({ page: 1, limit: 1000 }));
  }, [dispatch]);

  /* =====================================================
     HANDLERS & HELPER FUNCTIONS
  ====================================================== */
  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page)); };
  const handleFilterChange = (val) => { setActiveFilter(val); dispatch(setCurrentPage(1)); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? (checked ? "active" : "inactive") : value,
      };

      // Reset assigned instructor if the course scope shifts to preserve data layer consistency
      if (name === "course_id") {
        updated.instructor_id = "";
      }
      return updated;
    });
  };

  // --- Schedule Helpers ---
  const addScheduleRow = () => {
    setFormData((prev) => ({
      ...prev,
      schedule: [...prev.schedule, { day_of_week: "Monday", start_time: "", end_time: "" }],
    }));
  };
  const handleScheduleChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.schedule];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, schedule: updated };
    });
  };
  const removeScheduleRow = (index) => {
    if (formData.schedule.length > 1) {
      setFormData((prev) => ({ ...prev, schedule: prev.schedule.filter((_, i) => i !== index) }));
    }
  };

  // --- Student Multiple Selector ---
  const toggleStudent = (id) => {
    const targetId = Number(id);
    setFormData((prev) => {
      const current = prev.students ? [...prev.students].map(Number) : [];
      const already = current.includes(targetId);
      return { ...prev, students: already ? current.filter((sid) => sid !== targetId) : [...current, targetId] };
    });
  };
  const removeStudent = (id) => {
    const targetId = Number(id);
    setFormData((prev) => ({ ...prev, students: (prev.students || []).map(Number).filter((sid) => sid !== targetId) }));
  };

  const getStudentName = (student) => student.student_name || student.name || student.full_name || "Unnamed Student";
  const getStudentInitial = (student) => (getStudentName(student)[0] || "?").toUpperCase();

  // Resolve the display name(s) of the student(s) attached to a session row (used in the list/table view)
  const getSessionStudentNames = (session) => {
    const ids = (session.students || []).map(Number);
    if (ids.length === 0) return "No student assigned";
    const names = globalStudents
      .filter((s) => ids.includes(Number(s.id)))
      .map((s) => getStudentName(s));
    return names.length ? names.join(", ") : "No student assigned";
  };

  const formatDateDisplay = (d) => {
    if (!d) return "TBD";
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  /* =====================================================
     MODAL LAYOUT TRIGGERS
  ====================================================== */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setStudentSearch("");
    setInstructorSearch("");
    setActiveTab("details");
    setShowModal(true);
  };

  const openEditModal = (session) => {
    setIsEditMode(true);
    dispatch(setSelectedSessionMaster(session));
    setFormData({
      ...initialFormState,
      ...session,
      status: session.status || "active",
      schedule: session.schedule || initialFormState.schedule,
      students: session.students ? session.students.map(Number) : [],
      instructor_id: session.instructor_id || "",
    });
    setStudentSearch("");
    setInstructorSearch("");
    setActiveTab("details");
    setShowModal(true);
  };

  /* =====================================================
     FORM SUBMIT
  ====================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.session_name) {
      toastr.warning("Session name is required.");
      return;
    }
    if (!formData.course_id) {
      toastr.warning("Course choice validation parameter required before saving registry configurations.");
      setActiveTab("details");
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await dispatch(updateSessionMaster({ id: selectedSessionMaster.id, sessionMasterData: formData })).unwrap();
        toastr.success("Session updated.");
      } else {
        await dispatch(addSessionMaster(formData)).unwrap();
        toastr.success("Session created.");
      }
      setShowModal(false);
      dispatch(fetchSessionMasters({
        page: currentPage,
        limit,
        search: debouncedSearch,
        status: activeFilter === "all" ? "" : activeFilter,
      }));
    } catch (err) {
      toastr.error(err || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
      <button
        key={i}
        type="button"
        onClick={() => handlePageChange(i)}
        className={`sm-page-btn${currentPage === i ? " active" : ""}`}
      >
        {i}
      </button>
    ));

  const selectedStudentIds = (formData.students || []).map(Number);
  const filteredStudents = globalStudents.filter((s) => getStudentName(s).toLowerCase().includes(studentSearch.toLowerCase()));
  const selectedStudentObjects = globalStudents.filter((s) => selectedStudentIds.includes(Number(s.id)));
  const scheduleFilledCount = formData.schedule.filter((r) => r.start_time && r.end_time).length;

  /* =====================================================
     DYNAMIC INSTRUCTOR FILTERING LOGIC
  ====================================================== */
  // Filter down directory tracking rows explicitly mapped to the active course selection ID parameter
  const dynamicallyFilteredInstructors = globalInstructors.filter((coach) => {
    if (!formData.course_id) return false;

    // Normalize and search across single values or compound dataset maps
    const courseMatch = String(coach.course_id) === String(formData.course_id);
    const complexMapMatch = Array.isArray(coach.course_ids) && coach.course_ids.map(String).includes(String(formData.course_id));

    const searchMatch = (coach.full_name || coach.name || "")
      .toLowerCase()
      .includes(instructorSearch.toLowerCase());

    return (courseMatch || complexMapMatch) && searchMatch;
  });

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="sm-page">
      <div className="sm-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="sm-header">
          <div>
            <p className="sm-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="sm-title">Session Master</h1>
          </div>
          <button type="button" className="sm-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> New Session
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="sm-kpi-grid">
          {[
            { label: "Total sessions", value: total },
            { label: "Showing", value: sessionMasters.length },
            { label: "Current page", value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="sm-kpi">
              <span className="sm-kpi-label">{label}</span>
              <span className="sm-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="sm-toolbar">
          <div className="sm-search">
            <i className="fas fa-search sm-search-icon"></i>
            <input
              type="text"
              placeholder="Search by session name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="sm-filter-tabs">
            {[{ id: "all", name: "All" }, { id: "active", name: "Active" }, { id: "inactive", name: "Inactive" }].map(({ id, name }) => (
              <button
                key={id}
                type="button"
                className={`sm-ftab${activeFilter === id ? " active" : ""}`}
                onClick={() => handleFilterChange(id)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* ── DATA TABLE ── */}
        <div className="sm-card">
          <div className="table-responsive">
            <table className="sm-table">
              <thead>
                <tr>
                  <th style={{ width: "28%" }}>Session</th>
                  <th style={{ width: "12%" }}>Category</th>
                  <th style={{ width: "10%" }}>Mode</th>
                  <th style={{ width: "16%" }}>Start date</th>
                  <th style={{ width: "16%" }}>End date</th>
                  <th style={{ width: "8%" }}>Max</th>
                  <th style={{ width: "10%" }}>Status</th>
                  <th className="text-end" style={{ width: "10%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <Skeleton w={34} h={34} r={8} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="65%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td><Skeleton w={60} h={12} /></td>
                      <td><Skeleton w={50} h={22} r={20} /></td>
                      <td><Skeleton w={80} h={12} /></td>
                      <td><Skeleton w={80} h={12} /></td>
                      <td><Skeleton w={30} h={12} /></td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={52} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : sessionMasters.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="sm-empty-state">
                      <div className="sm-empty-icon-wrap">
                        <i className="fas fa-calendar-xmark"></i>
                      </div>
                      <p className="sm-empty-title">No sessions found</p>
                      <span className="sm-empty-desc">Try a different search term or create a new session.</span>
                    </td>
                  </tr>
                ) : (
                  sessionMasters.map((session) => (
                    <tr key={session.id} className="sm-row">
                      <td>
                        <div className="sm-name-cell">
                          <div className="sm-avatar">{session.session_name?.[0]?.toUpperCase() || "S"}</div>
                          <div>
                            <div className="sm-name">{session.session_name}</div>
                            <div className="sm-loc">
                              {session.category === "1-1 Session" ? (
                                <>
                                  <i className="fas fa-user-graduate"></i> {getSessionStudentNames(session)}
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-location-dot"></i> {session.location || "—"}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="sm-pill pill-blue">{session.category || "General"}</span>
                      </td>
                      <td>
                        <span className={`sm-pill ${session.mode === "online" ? "pill-green" : "pill-amber"}`}>
                          {session.mode?.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className="sm-date-main">{formatDateDisplay(session.start_date)}</div>
                      </td>
                      <td>
                        <div className="sm-date-main">{formatDateDisplay(session.end_date)}</div>
                      </td>
                      <td>
                        <div className="sm-date-main">{session.max_students || "—"}</div>
                      </td>
                      <td>
                        <span className={`sm-pill ${session.status === "active" ? "pill-green" : "pill-coral"}`}>
                          {session.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-end">
                        <button type="button" className="sm-btn-outline" onClick={() => openEditModal(session)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="sm-footer">
            <span className="sm-count">{sessionMasters.length} of {total} records</span>
            <div className="sm-pagination">
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="sm-page-btn sm-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="sm-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="sm-modal">

            {/* Sidebar */}
            <div className="sm-modal-side">
              <div className="sm-modal-side-header">
                <p className="sm-modal-side-title">Session profile</p>
                <p className="sm-modal-side-sub">Configuration workspace</p>
              </div>
              <nav className="sm-modal-nav">
                {[
                  { key: "details", icon: "fas fa-sliders-h", label: "Session details" },
                  { key: "schedule", icon: "fas fa-clock", label: "Schedule" },
                  { key: "instructor", icon: "fas fa-user-graduate", label: "Instructor" },
                  { key: "students", icon: "fas fa-user-graduate", label: "Roster" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`sm-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                    {key === "schedule" && scheduleFilledCount > 0 && <span className="sm-nav-badge">{scheduleFilledCount}</span>}
                    {key === "students" && selectedStudentIds.length > 0 && <span className="sm-nav-badge">{selectedStudentIds.length}</span>}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="sm-modal-main">
              <div className="sm-modal-header">
                <div>
                  <h2 className="sm-modal-title">{isEditMode ? "Edit session" : "New session"}</h2>
                  <p className="sm-modal-sub">
                    {isEditMode ? "Update the details below and save." : "Fill in the details below to schedule a new session."}
                  </p>
                </div>
                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)} aria-label="Close" disabled={saving}></button>
              </div>

              <div className="sm-modal-body">
                <div className="row g-3">

                  {/* ── SESSION DETAILS ── */}
                  {activeTab === "details" && (
                    <>
                      <div className="col-md-6">
                        <label className="sm-label">Session name *</label>
                        <input type="text" name="session_name" className="sm-field" placeholder="e.g. Weekend Beginners Batch" value={formData.session_name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Session type *</label>
                        <select name="category" className="sm-field" value={formData.category} onChange={handleChange}>
                          <option value="">Select type</option>
                          <option value="Group Session">Group session</option>
                          <option value="1-1 Session">1-1 session</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Course *</label>
                        <select name="course_id" className="sm-field" value={formData.course_id} onChange={handleChange}>
                          <option value="">Select course</option>
                          {globalCategories.map((course) => (
                            <option key={course.id} value={course.id}>{course.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Branch *</label>
                        <select name="branch_id" className="sm-field" value={formData.branch_id} onChange={handleChange}>
                          <option value="">Select branch</option>
                          {globalBranches.map((b) => (
                            <option key={b.id} value={b.id}>{b.branch_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Mode</label>
                        <select name="mode" className="sm-field" value={formData.mode} onChange={handleChange}>
                          <option value="offline">Offline</option>
                          <option value="online">Online</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Maximum students</label>
                        <input type="number" name="max_students" className="sm-field" placeholder="0" value={formData.max_students} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">Start date</label>
                        <input type="date" name="start_date" className="sm-field" value={formData.start_date} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="sm-label">End date</label>
                        <input type="date" name="end_date" className="sm-field" value={formData.end_date} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="sm-label">Location</label>
                        <input type="text" name="location" className="sm-field" placeholder="Full address or hall name" value={formData.location} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="sm-label">Meeting link</label>
                        <input type="text" name="meeting_link" className="sm-field" placeholder="https://…" value={formData.meeting_link} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <div className="sm-status-toggle-row">
                          <div>
                            <label className="sm-label" style={{ margin_bottom: 2 }}>Status</label>
                            <span className="sm-status-toggle-sub">Inactive sessions are hidden from new bookings.</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input sm-switch" type="checkbox" name="status" checked={formData.status === "active"} onChange={handleChange} />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── SCHEDULE ── */}
                  {activeTab === "schedule" && (
                    <>
                      <div className="col-12">
                        <div className="sm-section-note">
                          Set the recurring weekly timings for this session. Add a row for every day it runs.
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="sm-cat-table-head">
                          <span className="sm-cat-col-no">#</span>
                          <span className="sm-cat-col-day">Day</span>
                          <span className="sm-cat-col-time">Start</span>
                          <span className="sm-cat-col-time">End</span>
                          <span className="sm-cat-col-action"></span>
                        </div>
                        <div className="sm-cat-list">
                          {formData.schedule.map((row, index) => (
                            <div key={index} className="sm-cat-row">
                              <span className="sm-cat-col-no sm-cat-serial">{index + 1}</span>
                              <div className="sm-cat-col-day">
                                <select className="sm-field" value={row.day_of_week} onChange={(e) => handleScheduleChange(index, "day_of_week", e.target.value)}>
                                  <option>Monday</option>
                                  <option>Tuesday</option>
                                  <option>Wednesday</option>
                                  <option>Thursday</option>
                                  <option>Friday</option>
                                  <option>Saturday</option>
                                  <option>Sunday</option>
                                </select>
                              </div>
                              <div className="sm-cat-col-time">
                                <input type="time" className="sm-field" value={row.start_time} onChange={(e) => handleScheduleChange(index, "start_time", e.target.value)} />
                              </div>
                              <div className="sm-cat-col-time">
                                <input type="time" className="sm-field" value={row.end_time} onChange={(e) => handleScheduleChange(index, "end_time", e.target.value)} />
                              </div>
                              <div className="sm-cat-col-action">
                                <button
                                  type="button"
                                  className="sm-cat-remove-btn"
                                  disabled={formData.schedule.length === 1}
                                  onClick={() => removeScheduleRow(index)}
                                  title={formData.schedule.length === 1 ? "At least one schedule row is required" : "Remove"}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button type="button" className="sm-cat-add-btn" onClick={addScheduleRow}>
                          <i className="fas fa-plus"></i> Add schedule row
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── INSTRUCTOR ── */}
                  {activeTab === "instructor" && (
                    <>
                      <div className="col-12">
                        <div className="sm-section-note">
                          {!formData.course_id
                            ? "⚠️ Action Required: Select a base Course configuration inside the Details tab to view matching available instructors."
                            : "Choose the primary instructor assigned to this course structure."
                          }
                        </div>
                      </div>
                      {formData.course_id && (
                        <div className="col-12">
                          <input
                            type="text"
                            className="sm-field"
                            placeholder="Filter instructor directory…"
                            value={instructorSearch}
                            onChange={(e) => setInstructorSearch(e.target.value)}
                            style={{ marginBottom: 14 }}
                          />
                          <div style={{ maxHeight: 300, overflowY: "auto" }} className="sm-people-scroll">
                            {dynamicallyFilteredInstructors.length === 0 ? (
                              <div className="sm-roster-empty">
                                <i className="fas fa-chalkboard-user"></i>
                                No qualified instructors match the selected course criterion.
                              </div>
                            ) : (
                              <div className="sm-people-grid">
                                {dynamicallyFilteredInstructors.map((coach) => {
                                    const currentInstructorId = Number(coach.instructor_id || coach.id);
                                    const isChecked = Number(formData.instructor_id) === currentInstructorId;
                                    const name = coach.full_name || coach.name || "Unnamed";
                                    return (
                                      <label key={currentInstructorId} className={`sm-people-card${isChecked ? " selected" : ""}`} htmlFor={`instructor-${currentInstructorId}`}>
                                        <input
                                          type="radio"
                                          name="instructor_id"
                                          id={`instructor-${currentInstructorId}`}
                                          checked={isChecked}
                                          value={currentInstructorId}
                                          onChange={(e) => setFormData((prev) => ({ ...prev, instructor_id: Number(e.target.value) }))}
                                        />
                                        <div className="sm-people-avatar">{(name[0] || "?").toUpperCase()}</div>
                                        <div className="sm-people-copy">
                                          <div className="sm-people-name">{name}</div>
                                          <div className="sm-people-meta">{coach.mobile_no || "No contact on file"}</div>
                                        </div>
                                        <div className="sm-people-check"><i className="fas fa-check"></i></div>
                                      </label>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* ── STUDENTS ROSTER ── */}
                  {activeTab === "students" && (
                    <>
                      <div className="col-12">
                        <div className="sm-section-note">
                          Add or remove students on the roster for this session.
                        </div>
                      </div>
                      <div className="col-12">
                        <input
                          type="text"
                          className="sm-field"
                          placeholder="Search student directory…"
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                          style={{ marginBottom: 12 }}
                        />
                        <div className="sm-picker-count">
                          <b>{selectedStudentIds.length}</b> student{selectedStudentIds.length === 1 ? "" : "s"} selected
                        </div>
                        {selectedStudentObjects.length > 0 && (
                          <div className="sm-chip-row">
                            {selectedStudentObjects.map((student) => (
                              <span key={student.id} className="sm-chip">
                                <span className="sm-chip-avatar">{getStudentInitial(student)}</span>
                                {getStudentName(student)}
                                <button type="button" onClick={() => removeStudent(student.id)} aria-label={`Remove ${getStudentName(student)}`}>
                                  <i className="fas fa-xmark" style={{ fontSize: "0.6rem" }}></i>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ maxHeight: 300, overflowY: "auto" }} className="sm-people-scroll">
                          {globalStudents.length === 0 ? (
                            <div className="sm-roster-empty">
                              <i className="fas fa-user-graduate"></i>
                              No students registered yet.
                            </div>
                          ) : filteredStudents.length === 0 ? (
                            <div className="sm-roster-empty">
                              <i className="fas fa-magnifying-glass"></i>
                              No students match "{studentSearch}".
                            </div>
                          ) : (
                            <div className="sm-people-grid">
                              {filteredStudents.map((student) => {
                                const studentId = Number(student.id);
                                const isChecked = selectedStudentIds.includes(studentId);
                                return (
                                  <label key={studentId} className={`sm-people-card${isChecked ? " selected" : ""}`} htmlFor={`student-check-${studentId}`}>
                                    <input type="checkbox" id={`student-check-${studentId}`} checked={isChecked} onChange={() => toggleStudent(studentId)} />
                                    <div className="sm-people-avatar">{getStudentInitial(student)}</div>
                                    <div className="sm-people-copy">
                                      <div className="sm-people-name">{getStudentName(student)}</div>
                                      <div className="sm-people-meta">ID: {student.admission_number || "N/A"}</div>
                                    </div>
                                    <div className="sm-people-check"><i className="fas fa-check"></i></div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER with loader ── */}
              <div className="sm-modal-footer">
                <button type="button" className="sm-btn-cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="sm-btn-primary sm-btn-submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Saving changes…" : "Creating…"}
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Create session"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        @keyframes sm-spin { to { transform: rotate(360deg); } }
        @keyframes sm-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes sm-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes sm-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        /* ── BASE ── */
        .sm-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          color: #1d1d1f;
          font-size: 14px;
        }
        .sm-wrap { max-width: 1200px; margin: 0 auto; padding: 2rem 1.5rem; }

        /* ── PAGE HEADER ── */
        .sm-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
        .sm-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #aeaeb2; margin: 0 0 4px; }
        .sm-title { font-size: 1.35rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.022em; margin: 0; }

        /* ── KPI STRIP ── */
        .sm-kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 1.25rem; }
        @media (max-width: 640px) { .sm-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        .sm-kpi { background: #fff; border: 1px solid #d2d2d7; border-radius: 12px; padding: 14px 18px; display: flex; flex-direction: column; gap: 5px; }
        .sm-kpi-label { font-size: 11px; color: #6e6e73; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; }
        .sm-kpi-val { font-size: 1.5rem; font-weight: 600; color: #1d1d1f; letter-spacing: -0.025em; font-variant-numeric: tabular-nums; min-height: 1.8rem; display: flex; align-items: center; }

        /* ── TOOLBAR ── */
        .sm-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; flex-wrap: wrap; }
        .sm-search { flex: 1; min-width: 200px; position: relative; }
        .sm-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #aeaeb2; font-size: 12px; }
        .sm-search input {
          width: 100%; background: #fff; border: 1px solid #d2d2d7; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: #1d1d1f; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-search input:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-search input::placeholder { color: #aeaeb2; }

        .sm-filter-tabs {
          flex: 1; min-width: 0; display: flex; gap: 3px; overflow-x: auto;
          background: #fff; border: 1px solid #d2d2d7; border-radius: 10px; padding: 3px; scrollbar-width: none;
        }
        .sm-filter-tabs::-webkit-scrollbar { display: none; }
        .sm-ftab {
          border: none; background: transparent; padding: 5px 13px; border-radius: 7px;
          font-size: 12.5px; color: #6e6e73; cursor: pointer; font-weight: 400;
          white-space: nowrap; flex-shrink: 0; height: 28px; display: inline-flex; align-items: center;
          transition: all 0.12s; font-family: inherit;
        }
        .sm-ftab.active { background: #f5f5f7; color: #1d1d1f; font-weight: 500; border: 1px solid #d2d2d7; }
        .sm-ftab:hover:not(.active) { color: #3c3c43; }

        /* ── PRIMARY BUTTON ── */
        .sm-btn-primary {
          background: #0071e3; color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s, opacity 0.15s; white-space: nowrap; font-family: inherit;
          min-width: 140px; justify-content: center;
        }
        .sm-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .sm-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .sm-btn-submit { padding: 9px 22px; min-width: 158px; }

        /* ── TABLE CARD ── */
        .sm-card { background: #fff; border: 1px solid #d2d2d7; border-radius: 14px; overflow: hidden; }
        .sm-table { width: 100%; border-collapse: collapse; }
        .sm-table thead th {
          background: #f5f5f7; font-size: 10.5px; font-weight: 600; color: #6e6e73;
          text-transform: uppercase; letter-spacing: 0.07em; padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed; white-space: nowrap;
        }
        .sm-table tbody tr { border-bottom: 1px solid #f5f5f7; transition: background 0.1s; }
        .sm-table tbody tr:last-child { border-bottom: none; }
        .sm-row:hover { background: #fafafa; }
        .sm-table td { padding: 13px 18px; vertical-align: middle; }

        .sm-name-cell { display: flex; align-items: center; gap: 10px; }
        .sm-avatar {
          width: 34px; height: 34px; border-radius: 8px; background: #f5f5f7; border: 1px solid #e8e8ed;
          color: #3c3c43; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .sm-loc { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }
        .sm-loc i { font-size: 10.5px; margin-right: 2px; }

        /* ── PILLS ── */
        .sm-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; white-space: nowrap; }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        .sm-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }

        /* ── OUTLINE BUTTON ── */
        .sm-btn-outline {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        /* ── EMPTY STATE ── */
        .sm-empty-state { text-align: center; padding: 3rem 1rem; }
        .sm-empty-icon-wrap {
          width: 52px; height: 52px; background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center; font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .sm-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .sm-empty-desc { font-size: 12px; color: #aeaeb2; }

        /* ── PAGINATION ── */
        .sm-footer { display: flex; align-items: center; justify-content: space-between; padding: 12px 18px; border-top: 1px solid #f5f5f7; }
        .sm-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .sm-pagination { display: flex; gap: 4px; }
        .sm-page-btn {
          min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid #e8e8ed; background: #fff;
          border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; font-family: inherit;
        }
        .sm-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .sm-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .sm-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── MODAL ── */
        .sm-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: sm-overlay-in 0.2s ease;
        }
        .sm-modal {
          width: 100%; max-width: 900px; height: 590px; background: #fff; border-radius: 18px; border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06);
          display: flex; overflow: hidden; animation: sm-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        /* Sidebar */
        .sm-modal-side { width: 210px; background: #f5f5f7; border-right: 1px solid #e8e8ed; display: flex; flex-direction: column; flex-shrink: 0; }
        .sm-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .sm-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .sm-modal-side-sub { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .sm-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .sm-nav-item {
          display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400; cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; position: relative; font-family: inherit;
        }
        .sm-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .sm-nav-item span { flex: 1; }
        .sm-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .sm-nav-item.active { background: #fff; color: #0071e3; font-weight: 500; border: 1px solid #e8e8ed; }
        .sm-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }
        .sm-nav-badge { background: #0071e3; color: #fff; font-size: 10px; font-weight: 600; border-radius: 20px; padding: 1px 6px; line-height: 1.6; flex-shrink: 0; }

        /* Main panel */
        .sm-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .sm-modal-header { padding: 18px 26px; border-bottom: 1px solid #f5f5f7; display: flex; align-items: flex-start; justify-content: space-between; }
        .sm-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; letter-spacing: -0.015em; }
        .sm-modal-sub { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .sm-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .sm-modal-footer { padding: 14px 26px; border-top: 1px solid #f5f5f7; display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: #f5f5f7; }

        /* ── Form fields ── */
        .sm-label { display: block; font-size: 11px; font-weight: 600; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 5px; }
        .sm-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px; padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; font-family: inherit;
        }
        .sm-field:focus { border-color: #0071e3; box-shadow: 0 0 0 3px rgba(0,113,227,0.15); }
        .sm-field::placeholder { color: #aeaeb2; }
        select.sm-field { cursor: pointer; }

        .sm-section-note {
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 10px 14px;
          font-size: 12.5px; color: #6e6e73; line-height: 1.6;
        }

        .sm-status-toggle-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 9px; padding: 12px 16px;
        }
        .sm-status-toggle-sub { font-size: 11.5px; color: #aeaeb2; }

        /* ── SCHEDULE TABLE ── */
        .sm-cat-table-head {
          display: flex; align-items: center; gap: 10px; padding: 0 0 8px 0; border-bottom: 1px solid #e8e8ed;
          margin-bottom: 8px; font-size: 10px; font-weight: 700; color: #aeaeb2; text-transform: uppercase; letter-spacing: 0.07em;
        }
        .sm-cat-col-no { flex: 0 0 36px; text-align: center; }
        .sm-cat-col-day { flex: 1.3; }
        .sm-cat-col-time { flex: 1; }
        .sm-cat-col-action { flex: 0 0 36px; }
        .sm-cat-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 10px; max-height: 260px; overflow-y: auto; padding-right: 2px; }
        .sm-cat-list::-webkit-scrollbar { width: 3px; }
        .sm-cat-list::-webkit-scrollbar-track { background: transparent; }
        .sm-cat-list::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 99px; }
        .sm-cat-row { display: flex; align-items: center; gap: 10px; }
        .sm-cat-serial {
          flex: 0 0 36px; text-align: center; font-size: 12px; color: #aeaeb2; font-variant-numeric: tabular-nums;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 7px; height: 38px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .sm-cat-remove-btn {
          flex: 0 0 36px; width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center;
          border: 1px solid #fecaca; background: #fff; color: #ef4444; border-radius: 8px; cursor: pointer;
          font-size: 11.5px; transition: all 0.12s; flex-shrink: 0;
        }
        .sm-cat-remove-btn:hover:not(:disabled) { background: #fef2f2; border-color: #ef4444; }
        .sm-cat-remove-btn:disabled { opacity: 0.3; cursor: not-allowed; border-color: #e8e8ed; color: #d2d2d7; }
        .sm-cat-add-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; padding: 9px 16px;
          border: 1.5px dashed #d2d2d7; background: transparent; border-radius: 9px; font-size: 13px; font-weight: 500;
          color: #6e6e73; cursor: pointer; transition: all 0.15s; margin-top: 4px; font-family: inherit;
        }
        .sm-cat-add-btn:hover { border-color: #0071e3; color: #0071e3; background: #e8f0fe; }
        .sm-cat-add-btn i { font-size: 11px; }

        /* ── PEOPLE PICKER ── */
        .sm-picker-count { color: #6e6e73; font-size: 12px; margin-bottom: 10px; }
        .sm-picker-count b { color: #1d1d1f; font-weight: 600; }
        .sm-chip-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
        .sm-chip {
          display: inline-flex; align-items: center; gap: 7px; background: #e8f0fe; color: #1e40af;
          border-radius: 20px; padding: 5px 6px 5px 6px; font-size: 12px; font-weight: 500;
        }
        .sm-chip-avatar { width: 19px; height: 19px; border-radius: 50%; background: #0071e3; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; }
        .sm-chip button { background: none; border: none; color: #1e40af; display: flex; align-items: center; justify-content: center; padding: 0; width: 17px; height: 17px; border-radius: 50%; transition: background 0.12s; }
        .sm-chip button:hover { background: rgba(30,64,175,0.15); }

        .sm-people-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
        .sm-people-card {
          display: flex; align-items: center; gap: 10px; border: 1.5px solid #e8e8ed; border-radius: 10px; padding: 10px 12px;
          cursor: pointer; transition: all 0.12s; background: #fff; position: relative;
        }
        .sm-people-card:hover { border-color: #d2d2d7; background: #fafafa; }
        .sm-people-card.selected { border-color: #0071e3; background: #e8f0fe; }
        .sm-people-avatar {
          width: 34px; height: 34px; border-radius: 50%; background: #f5f5f7; color: #3c3c43; display: flex; align-items: center;
          justify-content: center; font-weight: 600; font-size: 13px; flex-shrink: 0; transition: all 0.12s;
        }
        .sm-people-card.selected .sm-people-avatar { background: #0071e3; color: #fff; }
        .sm-people-copy { min-width: 0; flex-grow: 1; }
        .sm-people-name { font-weight: 500; font-size: 12.5px; color: #1d1d1f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sm-people-meta { font-size: 11px; color: #6e6e73; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sm-people-check {
          width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid #e8e8ed; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; transition: all 0.12s; background: #fff;
        }
        .sm-people-card.selected .sm-people-check { background: #0071e3; border-color: #0071e3; color: #fff; }
        .sm-people-check i { font-size: 0.55rem; opacity: 0; transition: opacity 0.12s; }
        .sm-people-card.selected .sm-people-check i { opacity: 1; }
        .sm-people-card input { position: absolute; opacity: 0; pointer-events: none; }

        .sm-people-scroll::-webkit-scrollbar { width: 4px; }
        .sm-people-scroll::-webkit-scrollbar-track { background: transparent; }
        .sm-people-scroll::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        .sm-roster-empty { padding: 2.5rem 1.5rem; text-align: center; color: #6e6e73; font-size: 13px; }
        .sm-roster-empty i { font-size: 1.4rem; color: #d2d2d7; margin-bottom: 10px; display: block; }

        /* ── CANCEL BUTTON ── */
        .sm-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff; border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73; cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .sm-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }
        .sm-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── MODAL BODY SCROLLBAR ── */
        .sm-modal-body::-webkit-scrollbar { width: 5px; }
        .sm-modal-body::-webkit-scrollbar-track { background: transparent; }
        .sm-modal-body::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .sm-modal { flex-direction: column; height: auto; max-height: 90vh; }
          .sm-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .sm-modal-side-header { display: none; }
          .sm-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; scrollbar-width: none; }
          .sm-modal-nav::-webkit-scrollbar { display: none; }
          .sm-nav-item { white-space: nowrap; width: auto; font-size: 12px; padding: 7px 12px; }
          .sm-modal-body { padding: 16px; }
          .sm-modal-footer { padding: 12px 16px; }
          .sm-modal-header { padding: 14px 16px; }
          .sm-people-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .sm-wrap { padding: 1rem; }
          .sm-header { flex-direction: column; }
          .sm-btn-primary { width: 100%; justify-content: center; }
          .sm-toolbar { flex-direction: column; }
          .sm-filter-tabs { width: 100%; }
          .sm-modal-footer { flex-wrap: wrap; }
          .sm-btn-submit { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default SessionMaster;
